import { ol_Extent, ol_Feature } from "@omnia/exports/openlayers"
import { NumericValueColorProvider } from "../color/provider"
import { ICrop, IVariety } from "../crops"
import { ImportDocument } from "../documents"
import { FieldViewModel } from "../fields"
import { IFieldsMap, IFieldsMapContainerContext, IFieldsMapContainerOptions } from "../fields/map"
import { GenericPlanUnitId, LayerStorageMode, LayerTypeGroupId, LayerTypeId, LayerTypeSubgroupId, LayerValueType, LayerZoneTypeId, MapGeometryType, MapRegularShapeType, MapScope, SatImageType, TerraMapLayerType } from "../generated/enums"
import { Geometry2, WellKnownTextString } from "../geom"
import { Layer } from "../map/layer"
import { IMapMeasuringService } from "../map/measuring/types"
import { IMapTopologyService } from "../map/topo/types"
import { ILayerOptions, IMapKeyEntry, OpenLayersStyleProvider } from "../map/types"
import { ICropYieldScale } from "../models/yield"
import { ResourceMetadata } from "../resources/types"
import { SharedDataService } from "../sharedData"
import { IComponentLauncher } from "../shell/types"
import { SoilTextureProfile } from "../soil/texture"
import { IOmniaService } from "../types"
import { Datalayer } from "./datalayer"
import { DatalayersMap } from "./datalayersMap"
import { LayerViewModel } from "./layerViewModel"

export interface IDataLayerType {

    TypeId: LayerTypeId

    GroupId: LayerTypeGroupId
    SubgroupId: LayerTypeSubgroupId

    Name: string

    Scope: MapScope
    CroppingOptional: boolean

    ValueType: LayerValueType
    Units: string
    ValueLabel?: string

    DefaultRounding?: number

    Standalone: boolean
    Importable: boolean
    DateApplies: boolean
    YearApplies: boolean
    YearlyUnique: boolean
    IncludeYearInName: boolean
    ExcludeTypeFromName: boolean
    ExcludeCroppingFromName: boolean
    ZoneNumbersReadOnly: boolean

    LayerName?: {

        /**
        * true if a name must be provided for layers of this type
        */
        Required?: boolean

        /**
        * true if the name must be unique (for the current holding and cropping year)
        */
        Unique?: boolean

        NotAvailable?: boolean

        /**
        * Default template for the name (applied through the $parse service)
        */
        DefaultNameTemplate?: string

    }

    Values?: IDataLayerValue[]

    Style?: IDataLayerStyle,

    Ranges?: IDataLayerRange[]

    VisibleLabels?: {
        Feature: boolean,
        Tooltip: boolean,
        Key: boolean,
    },

    DeduplicateLabels?: boolean,

    DynamicNumericValueRange?: boolean,

    GenericFeatures?: boolean,

    AlternateStyles?: {
        Key: string
        Ranges: IDataLayerRange[]
    }[]

}

export interface IDataLayerStyle {
    Gradient?: number[][],
    ShowStroke?: boolean,
    ShowZoneNumbers?: boolean
}

export interface IDataLayerRange {
    Min: number,
    Max: number,
    FillColor: number[],
    Label: string
}

export interface IDataLayerValue {

    Value: any
    Data?: any
    Name: string
    Style?: {
        Colour: any
    }

}

export interface IMapFeatureStyle {

    Fill?: {
        Color: number[]
    }

    Stroke?: {
        Width?: number
    }

    LineWidth?: number
    LinePattern?: number

    AutoLabel?: boolean

}

export interface IMapLabelStyle {
    Position?: string
}

export interface IMapRegularShape {

    Type: MapRegularShapeType

    Center: [number, number]

    Angle?: number

    Size1?: number
    Size2?: number

}

export interface IDataLayerZone<GeometryType extends Geometry2 = Geometry2> {

    DataLayerZoneId: number

    LayerId?: number
    Id?: number

    Number: number
    Geometry: GeometryType
    Area?: number

    Value: number | IDataLayerValue
    InitValue?: number | IDataLayerValue

    TypeId?: LayerZoneTypeId

    Data?: { [key: string]: any }

    Label?: string
    LabelStyle?: IMapLabelStyle

    RegularShape?: IMapRegularShape
    Style?: IMapFeatureStyle

    DocumentId?: number
    Document?: ImportDocument

    Name?: string

    FieldId?: number

    Buffer?: number
    BufferLine?: GeometryType

}

export interface IGeorefControlPoint {
    Map: { X: number, Y: number },
    Raster: { X: number, Y: number },
}

export interface IGeorefPictureInfo {

    Path: string

    SourceDocumentId: number
    SourceUrl: string

    Extent: [number, number, number, number]

    ImageWidth: number
    ImageHeight: number
    ImageMimeType: string

    ControlPoints: IGeorefControlPoint[]

    BoundaryWkt: string

}

export interface IGenericPlan {

    GenericPlanId: number

    YearId: number

    Name: string
    ProductName: string
    Units: GenericPlanUnitId

    Labels: { [value: string]: string }

    MinRate: number
    MaxRate: number
    TotalQty: number

    CreatedBy: number
    CreatedOn: Date
    Metadata: ResourceMetadata

}

export interface IDataLayer {

    DataLayerId: number

    ParentId?: number
    Parent?: IDataLayer
    Children?: IDataLayer[]

    TypeId: number
    Type?: IDataLayerType

    SupportedValues?: IDataLayerValue[]

    CustomTypeId?: number

    StorageMode?: LayerStorageMode

    FirstYearId?: number

    Inactive?: boolean

    SubtypeId?: number
    Origin?: number

    Unit?: number
    Date?: Date

    CreatedOn?: Date
    UpdatedOn?: Date

    Calculated?: boolean

    ClipFeatures?: boolean

    GeometryType: MapGeometryType

    Name?: string
    Value?: string | IDataLayerValue

    FieldId?: number
    CroppingId?: number
    HoldingId?: number

    Zones: IDataLayerZone[]

    HasPointData?: boolean
    PointData?: number[][]

    LayerData?: any

    CropId?: number
    Crop?: ICrop
    VarietyId?: number
    Variety?: IVariety

    Divisions?: {
        Number: number,
        Min: number,
        Max: number
    }[],

    Points?: {
        Sum: number,
        Count: number,
        Min: number,
        Max: number,
        Distribution: { [key: number]: number }
    },

    GeorefPicture?: IGeorefPictureInfo

    GenericPlanId?: number
    GenericPlan?: IGenericPlan

    TerraMap?: TerraMapLayerType

    Satellite?: boolean
    SatelliteData?: {
        type: SatImageType
    }

    PkMorganAnalysis?: boolean

    Properties?: {

        SoilTextureProfile?: SoilTextureProfile

    }

}

export interface IDatalayersMap extends IFieldsMap {

    getTopmostLayer(predicate: (layer: Datalayer) => boolean): Datalayer

    requestStackOrderUpdate(delay?: number): void

}

export interface IDatalayersMapFactory {
    createMap(options?: IFieldsMapContainerOptions): IDatalayersMap
}


export interface ILayerViewModel {

    readonly datalayer: IDataLayer
    readonly datalayerId: number

    readonly fieldId: number
    readonly field: FieldViewModel
    readonly croppingId: number
    readonly cropping: FieldViewModel

    readonly typeId: LayerTypeId
    readonly type: IDataLayerType

    readonly mapLayer: IDatalayerMapInstance
    getMapLayer(create?: boolean): IDatalayerMapInstance

    readonly description: string

    visible: boolean
    showLabels: boolean
    alwaysVisible: boolean
    selectable: boolean
    showTooltips: boolean

    getVariants(): ILayerViewModel[]
    hasVariants(): boolean
    isVariant(): boolean
    readonly variantDescription: string

    getExtent(): ol_Extent
    setVisible(value: boolean): PromiseLike<any>
    setViewer(viewer: IDatalayerViewerFactory): void

    readonly zonesLoaded: boolean
    notifyOnLoad(fn: (l: ILayerViewModel) => void): void

    getZone(zoneId: number): IDatalayerZoneMapInstance

    dispose(): void
    refresh(): void
    refreshViewer(): void

    setLayerLabel(label: string): void

    canShowPoints(): boolean

}

export interface ILayerViewModelOptions {

    layerInstance?: IDatalayerMapInstance

    addToMap?: boolean
    selectable?: boolean

    /**
     * If true, will display a "loading" overlay over the layer
     * while it is being loaded
     */
    showOverlayOnLoad?: boolean

    /**
     * Initial value for the "layersPopup" property of the layer
     * If defined, will be set on the layer once available
     */
    layersPopup?: boolean

}

export interface IDatalayersLoader {

    getLayerTypes(): IDataLayerType[]

    getCustomLayerType(customTypeId: number, typeId: LayerTypeId): IDataLayerType

    loadLayerZones(layers: IDataLayer[], options?: { buffer?: boolean, blockUI?: boolean }): PromiseLike<IDataLayer[]>
    loadLayerPoints(layerId: number): PromiseLike<number[][]>

    getCropYieldScales(year?: number): ICropYieldScale[]
    getCropping(croppingId: number): FieldViewModel

    getTopologyService(): IMapTopologyService

    setupLayers(layers: IDataLayer[]): IDataLayer[]

}

export interface IDatalayerOptions extends ILayerOptions {
}

export interface IDatalayerContext {

    createMapLayerInstance(layer: IDataLayer, options?: IDatalayerOptions): IDatalayerMapInstance

    readonly loader: IDatalayersLoader
    readonly viewManager: IDatalayerViewManager
    readonly measuringService: IMapMeasuringService
    readonly sharedData: SharedDataService

    getAllLayers(): IDatalayerMapInstance[]
    getAllFields(): FieldViewModel[]

    /** Get or create a generic cache which can be shared by all layers sharing this context  */
    getCache<T>(cacheId: string): { [key: string]: T }

    onZoneValueChanged?(zone: IDatalayerZoneMapInstance): void

}

export type DatalayerFactoryFunction = (layer: IDataLayer, context: IDatalayerContext, options?: IDatalayerOptions) => IDatalayerMapInstance

export interface IDatalayerFactory {

    create: DatalayerFactoryFunction;

}

export interface IDatalayerZoneMapInstance {

    readonly layer: IDatalayerMapInstance

    readonly feature: ol_Feature
    value: any

    readonly id: number

    readonly zone: IDataLayerZone
    readonly zoneType: LayerZoneTypeId
    readonly zoneName?: string

    readonly area?: number

    readonly editMode: boolean

}

export interface IDatalayerMapInstance<Zone extends IDatalayerZoneMapInstance = IDatalayerZoneMapInstance> {

    readonly context: IDatalayerContext

    readonly type: IDataLayerType
    readonly typeId: LayerTypeId
    readonly customType: IDataLayerType

    readonly datalayer: IDataLayer
    readonly datalayerId: number

    readonly fieldId: number
    readonly croppingId: number

    readonly zones: Zone[]

    readonly points?: number[][]

    readonly editMode: LayerEditMode

    readonly cropId: number
    readonly crop: ICrop

    readonly labelsNoDeduplicate?: boolean

    readonly valueLabel: string
    readonly valueUnits?: string

    getViewer(): IDatalayerViewer
    setViewer(factory: IDatalayerViewerFactory)

    refresh()

    getFeatures(): ol_Feature[]
    findFeatures(predicate: (feature: ol_Feature) => boolean): ol_Feature[]

    getLayerStyleFunction(selected: boolean): OpenLayersStyleProvider

    asGenericMapLayer(): Layer

}

export interface IDatalayerViewerFactory {

    readonly name: string
    readonly typeId: LayerTypeId
    readonly key?: string

    createViewer(layer: IDatalayerMapInstance): IDatalayerViewer

    appliesToType?(type: IDataLayerType): boolean

}

export interface IDatalayerViewManager {

    getViewers(type: LayerTypeId): IDatalayerViewerFactory[];

    getCurrentViewer(type: LayerTypeId): IDatalayerViewerFactory;
    setCurrentViewer(type: LayerTypeId, viewer: IDatalayerViewerFactory): boolean;

}

export enum LayerEditMode {
    None = 0,
    InEditor = 1,
    EditTarget = 2
}

export enum ZoneTextLocation {
    MapFeature = 1,
    MapTooltip = 2,
    Key = 3,
    ZoneDescription = 4
}

export interface IDatalayerViewer {

    refresh(): void

    getZoneVisibility(zone: IDatalayerZoneMapInstance): boolean

    getZoneValue(zone: { value: any }): number
    getZoneText(zone: IDatalayerZoneMapInstance, location: ZoneTextLocation): string

    getFeatureFillColor(feature: ol_Feature): number[]
    getValueFillColor(value: any): number[]
    getValueText(value: any, location?: ZoneTextLocation): string

    getKey(): IDatalayerKey

    getPointFillColor(feature: any): number[]
    getClusterPointFillColor(values: any[]): number[]

    readonly showStroke?: boolean
    readonly units?: string

}

export interface IDatalayerKey {

    id: string
    name: string

    numeric: boolean
    fixed: boolean
    unit?: string

    typeKey: IDatalayerKeyItem[]
    layerKey: IDatalayerKeyItem[]

}

export type IDatalayerKeyItem = IMapKeyEntry & {
    value?: any
}

export interface INumericValuesViewerOptions {

    dataProperty?: string
    units?: string

    type?: IDataLayerType

    colorProvider?: NumericValueColorProvider
    ranges?: IDataLayerRange[]

    showStroke?: boolean

    layerFilter?: (layer: IDatalayerMapInstance) => boolean

    valueFilter?: (value: any) => boolean

    keyId?: {
        id: string
        name: string
    }

    debug?: boolean

}

export interface IDatalayerZoneUpdate extends IDataLayerZone<WellKnownTextString> {
}

export interface StartLayerEditorArgs {
    layer: IDataLayer
    field: FieldViewModel
    config?: LayerEditorConfig
}

export interface LayerEditorConfig {

    title?: string

    headerTemplateUrl?: string

    canEditFeatures?: boolean
    canChangeLayerType?: boolean
    availableLayerTypes?: IDataLayerType[]

    saveChanges?(target: IDatalayerEditModel): ng.IPromise<IDataLayer>

}

export interface SharedLayersService extends IOmniaService, IDatalayersLoader {

    readonly sharedData: SharedDataService
    readonly fields: FieldViewModel[]
    readonly cropping: FieldViewModel[]
    readonly crops: ICrop[]
    readonly cropVarieties: IVariety[]

    getFields(includeDisabled?: boolean): FieldViewModel[]
    getLayers(ids: number[], includeZones?: boolean): ng.IPromise<IDataLayer[]>

    getFieldLayers(fieldIds: number[], includeInactive?: boolean): ng.IPromise<IDataLayer[]>
    getHoldingLayers(types?: LayerTypeId[]): ng.IPromise<IDataLayer[]>
    getHoldingYieldLayers(): ng.IPromise<IDataLayer[]>

    getGenericPlans(yearId: number): IGenericPlan[]

    getLayerManagerEntries(): ng.IPromise<IDataLayer[]>

    deleteLayers(layerIds: number[], deleteAnalysis: boolean): ng.IPromise<any>

    startLayerEditor(args: StartLayerEditorArgs, context: IComponentLauncher): ng.IPromise<any>

}

export interface IDatalayerMapDependencies extends IFieldsMapContainerContext {

    readonly svc: SharedLayersService
    readonly loader: IDatalayersLoader
    readonly factory: IDatalayerFactory
    readonly viewManager: IDatalayerViewManager
    readonly topology: IMapTopologyService

    ensureLoaded(): ng.IPromise<any>

    createContext(getAllLayers: () => IDatalayerMapInstance[]): IDatalayerContext

}


export const DefaultLineWidth = 3
export const ThickLineWidth = 6

export enum LinePattern {
    Solid = 1,
    Dashes = 2,
    Dots = 3
}

export interface IGenericFeatureStyle {
    fillColor?: number[]
    showStroke?: boolean,
    lineWidth?: number,
    linePattern?: LinePattern
    autoLabel?: boolean
}

export interface CustomLayerStyle {

    CustomStyleId: number

    TypeId: number

    Ranges: IDataLayerType["Ranges"]

    SoilTextureProfile: SoilTextureProfile

}

export interface ILayerDetailsContext {
    readonly map: DatalayersMap
    readonly layers: LayerViewModel[]
    getSelectedField(): FieldViewModel
}

export interface IDatalayerEditModel extends Omit<IDataLayer, "Zones" | "Properties" | "SupportedValues"> {

    SessionHoldingId: number
    SessionYearId: number

    Zones: IDatalayerZoneUpdate[]

}
